"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSpotWorkerSummary = useSpotWorkerSummary;
const vue_1 = require("vue");
const types_1 = require("../../types");
const common_1 = require("../../../common/common");
const timeZones_1 = __importDefault(require("../../../common/timeZones"));
const useSpotShiftStore_1 = require("../../stores/useSpotShiftStore");
const useSpotWorkerStore_1 = require("../../stores/useSpotWorkerStore");
function useSpotWorkerSummary() {
    const shiftStore = (0, useSpotShiftStore_1.useSpotShiftStore)();
    const workerStore = (0, useSpotWorkerStore_1.useSpotWorkerStore)();
    const startDate = (0, vue_1.ref)(new Date());
    const endDate = (0, vue_1.ref)(new Date());
    const branchId = (0, vue_1.ref)(0);
    function setDates(start, end) {
        startDate.value = setTimeToStart(new Date(start));
        endDate.value = setTimeToEnd(new Date(end));
    }
    function setBranchId(bId) {
        branchId.value = bId;
    }
    function setTimeToStart(date) {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }
    function setTimeToEnd(date) {
        const d = new Date(date);
        d.setHours(23, 59, 59, 999);
        return d;
    }
    function isShiftInRange(shift) {
        const shiftStart = setTimeToStart(new Date(shift.start));
        const shiftEnd = setTimeToEnd(new Date(shift.end));
        return !(shiftEnd < startDate.value || shiftStart > endDate.value);
    }
    function calculateRequiredRows(shifts) {
        const dateCount = {};
        shifts.forEach((shift) => {
            if (shift.userId !== 0 || !isShiftInRange(shift)) {
                return;
            }
            const date = (0, common_1.yearMonthDateString)(new Date(shift.start), timeZones_1.default.ASIA_TOKYO);
            dateCount[date] = (dateCount[date] || 0) + 1;
        });
        return Math.max(...Object.values(dateCount), 0);
    }
    function initSpotworkerPlaceholders(rows) {
        return Array.from({ length: rows }, (_, i) => ({
            id: 0,
            tempId: `temp-${i + 1}`,
            email: "",
            furigana: "",
            name: `募集中..`,
            phone: "",
            memo: "",
            rating: 0,
            visibleInShift: true,
        }));
    }
    const shifts = (0, vue_1.computed)(() => {
        return Object.values(shiftStore.state.shifts);
    });
    const data = (0, vue_1.computed)(() => {
        const unclaimedShifts = shifts.value.filter((shift) => !shift.userId && isShiftInRange(shift));
        const numberOfRows = calculateRequiredRows(shifts.value);
        const tempSpotWorkers = initSpotworkerPlaceholders(numberOfRows);
        const claimedShiftMap = {};
        const claimedShifts = shifts.value.filter((shift) => shift.userId && isShiftInRange(shift));
        claimedShifts.forEach((s) => {
            if (!claimedShiftMap[s.userId]) {
                claimedShiftMap[s.userId] = [];
            }
            const userShifts = claimedShiftMap[s.userId];
            if (!userShifts) {
                return;
            }
            userShifts.push(s);
        });
        const workers = Object.values(workerStore.state.employees)
            .filter((e) => e.visibleInShift && claimedShiftMap[e.id]?.length)
            .map((worker) => ({ ...worker, tempId: "" }));
        const result = {
            spotWorkers: workers,
            tempSpotWorkers,
            [types_1.SpotShiftKey.CLAIMED]: distributeClaimedShifts(workers, claimedShiftMap),
            [types_1.SpotShiftKey.UNCLAIMED]: distributeUnClaimedShifts(numberOfRows, tempSpotWorkers, unclaimedShifts),
            numberOfRows,
        };
        return result;
    });
    function distributeClaimedShifts(workers, claimedShiftMap) {
        const out = {};
        out[branchId.value] = {};
        workers.forEach((w) => {
            const branchData = out[branchId.value];
            if (!branchData) {
                return;
            }
            if (!branchData[w.id]) {
                branchData[w.id] = {};
            }
            claimedShiftMap[w.id]?.forEach((s) => {
                if (!isShiftInRange(s)) {
                    return;
                }
                const date = (0, common_1.yearMonthDateString)(new Date(s.start), timeZones_1.default.ASIA_TOKYO);
                const userShifts = branchData[w.id];
                if (!userShifts) {
                    return;
                }
                if (!userShifts[date]) {
                    userShifts[date] = [];
                }
                userShifts[date].push({
                    id: s.id,
                    roleId: s.roleId,
                    userId: s.userId,
                    purpose: s.purpose,
                    tempUserId: 0,
                    timeLabel: (0, common_1.datesToKitchenTimeRange)(new Date(s.start), new Date(s.end)),
                });
            });
        });
        return out;
    }
    function distributeUnClaimedShifts(numberOfRows, tempSpotWorkers, shiftsData) {
        const out = {};
        const shiftsByDate = {};
        shiftsData.forEach((d) => {
            if (!isShiftInRange(d)) {
                return;
            }
            const date = (0, common_1.yearMonthDateString)(new Date(d.start), timeZones_1.default.ASIA_TOKYO);
            if (!shiftsByDate[date]) {
                shiftsByDate[date] = [];
            }
            shiftsByDate[date].push(d);
        });
        Object.entries(shiftsByDate).forEach(([date, shiftsForDate]) => {
            // Find workers that already have shifts on this date
            const workersWithShiftsOnDate = {};
            Object.entries(out[branchId.value] || {}).forEach(([workerId, dateShifts]) => {
                if (dateShifts && dateShifts[date]) {
                    workersWithShiftsOnDate[workerId] = true;
                }
            });
            // Allocate new shifts to empty cells
            let nextWorkerIndex = 0;
            shiftsForDate.forEach((d) => {
                // Find next worker without a shift on this date
                while (nextWorkerIndex < numberOfRows && workersWithShiftsOnDate[tempSpotWorkers[nextWorkerIndex]?.tempId || ""]) {
                    nextWorkerIndex++;
                }
                if (nextWorkerIndex >= numberOfRows) {
                    nextWorkerIndex = 0; // Wrap around if all cells are full
                }
                const workerKey = tempSpotWorkers[nextWorkerIndex]?.tempId || "";
                if (!workerKey) {
                    return;
                }
                // Initialize branch level
                if (!out[branchId.value]) {
                    out[branchId.value] = {};
                }
                const branchData = out[branchId.value];
                if (!branchData) {
                    return;
                }
                // Initialize worker level
                if (!branchData[workerKey]) {
                    branchData[workerKey] = {};
                }
                const workerShifts = branchData[workerKey];
                if (!workerShifts) {
                    return;
                }
                // Initialize date level
                if (!workerShifts[date]) {
                    workerShifts[date] = [];
                }
                const shiftsInCell = workerShifts[date];
                if (!shiftsInCell) {
                    return;
                }
                shiftsInCell.push({
                    id: d.id,
                    purpose: d.purpose,
                    roleId: d.roleId,
                    userId: 0,
                    tempUserId: 0,
                    timeLabel: (0, common_1.datesToKitchenTimeRange)(new Date(d.start), new Date(d.end)),
                });
                workersWithShiftsOnDate[workerKey] = true; // Mark this worker as having a shift
                nextWorkerIndex++;
            });
        });
        return out;
    }
    return {
        data,
        startDate,
        endDate,
        branchId,
        setDates,
        setBranchId,
    };
}
